import React from 'react';
import PropTypes from 'prop-types';

import {Grid2 as Grid, Box, Stack, Typography, Divider} from 'views/components';
import {Translate} from 'views/module';

import ClientAuthentication from './authentication/ClientAuthentication.jsx';
import EmailNotification from './notification/EmailNotification.jsx';
import SmsNotification from './notification/SmsNotification.jsx';
import SmsCommunication from './communication/SmsCommunication.jsx';
import ContactsSkeleton from './ContactsSkeleton.jsx';
import SmsDialpadIntegration from './dialpad_integration/SmsDialpadIntegration.jsx';

import {useMediaQuery, useTheme} from '@mui/material';
import {convertToE164} from 'utils/miscellaneous/e164';

const Contacts = (props) => {
  const {
    relationshipData,
    userIdentity,
    userNotificationChannels,
    userCommunication,
    userDialpadIntegration,
    clientBackend,
    clientAuthenticationLoading,
    clientNotificationLoading,
    clientCommunicationLoading,
    clientDialpadIntegrationLoading,
  } = props;

  const contactsTable = React.useMemo(() => {
    const contacts = relationshipData.contacts;
    const table = {
      emails: {},
      phoneNumbers: {},
      miscellaneous: {},
    };
    const contactMetadata = {
      authenticationStatus: null, // so that if they already have an authenticated email, other emails won't display button
      isSubscribedForNotification: false,
      communicationStatus: null,
      dialpadStatus: null,
    };
      // add every element from contacts into the table
    for (const contact of contacts) {
      if (contact.type === 'Email') {
        table.emails[contact.detail] = {...contactMetadata};
      } else if (contact.type === 'Phone') {
        table.phoneNumbers[contact.detail] = {...contactMetadata};
      } else {
        if (table.miscellaneous[contact.type]) {
          table.miscellaneous[contact.type].push(contact.detail);
        } else {
          table.miscellaneous[contact.type] = [contact.detail];
        }
      }
    }
    // now update emails and phoneNumbers with authentication/notification/communication metadata
    // or add if not already present in contacts list
    const subscribedEmails = userNotificationChannels?.emails;
    if (subscribedEmails && subscribedEmails.length > 0) {
      for (const {email: subscribedEmail} of subscribedEmails) {
        if (table.emails[subscribedEmail]) {
          table.emails[subscribedEmail].isSubscribedForNotification = true;
        } else {
          table.emails[subscribedEmail] = {...contactMetadata, isSubscribedForNotification: true};
        }
      }
    }

    const authenticatedEmail = userIdentity?.email;
    if (authenticatedEmail) {
      if (table.emails[authenticatedEmail]) {
        table.emails[authenticatedEmail].authenticationStatus = userIdentity.status;
      } else {
        table.emails[authenticatedEmail] = {...contactMetadata, authenticationStatus: userIdentity.status};
      }
    } else {
      // if they don't have an authenticated email, then we need to change the status of all emails to 'NOT_SET_UP'
      for (const [email, metadata] of Object.entries(table.emails)) {
        table.emails[email] = {...metadata, authenticationStatus: 'NOT_SET_UP'};
      }
    }

    const subscribedSmsNumbers = userNotificationChannels?.smsNumbers;
    if (subscribedSmsNumbers && subscribedSmsNumbers.length > 0) {
      for (const {phoneNumber: subscribedSmsNumber} of subscribedSmsNumbers) {
        if (table.phoneNumbers[subscribedSmsNumber]) {
          table.phoneNumbers[subscribedSmsNumber].isSubscribedForNotification = true;
        } else {
          table.phoneNumbers[subscribedSmsNumber] = {...contactMetadata, isSubscribedForNotification: true};
        }
      }
    }

    let communicationSms = userCommunication?.phoneNumber;
    if (communicationSms) {
      const conversion = convertToE164(communicationSms);
      if (conversion.isValid) {
        communicationSms = conversion.e164Format; // glue code to normalize with format of contact phone numbers
      }
      const communicationStatus = userCommunication.subscribed ? 'ACTIVE' : 'INACTIVE';
      if (table.phoneNumbers[communicationSms]) {
        table.phoneNumbers[communicationSms].communicationStatus = communicationStatus;
      } else {
        table.phoneNumbers[communicationSms] = {...contactMetadata, communicationStatus};
      }
    } else {
      for (const [phoneNumber, metadata] of Object.entries(table.phoneNumbers)) {
        table.phoneNumbers[phoneNumber] = {...metadata, communicationStatus: 'NOT_SET_UP'};
      }
    }
    const dialpadSms = userDialpadIntegration?.phoneNumber;
    if (dialpadSms === 'CLIENT_ALREADY_REGISTERED') {
      // leave dialpadStatus as null
    } else if (dialpadSms) {
      if (table.phoneNumbers[userDialpadIntegration.phoneNumber]) {
        table.phoneNumbers[dialpadSms].dialpadStatus = 'ACTIVE';
      } else {
        table.phoneNumbers[dialpadSms] = {...contactMetadata, dialpadStatus: 'ACTIVE'};
      }
    } else {
      for (const [phoneNumber, metadata] of Object.entries(table.phoneNumbers)) {
        table.phoneNumbers[phoneNumber] = {...metadata, dialpadStatus: 'NOT_SET_UP'};
      }
    }
    return table;
  }, [userIdentity, relationshipData.contacts, userNotificationChannels, userCommunication, userDialpadIntegration]);

  const isBreakpointLg = useMediaQuery(useTheme().breakpoints.up('lg'));

  const areChannelsLoading = clientAuthenticationLoading || clientNotificationLoading || clientCommunicationLoading || clientDialpadIntegrationLoading;

  return areChannelsLoading ? (
    <ContactsSkeleton />
  ) : (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Typography
          sx={{
            fontWeight: 600,
          }}
        >
          <Translate expression='client.ClientDetail.Relationships.Contacts.Title' />
        </Typography>
      </Grid>
      <Grid size={12}>
        <Divider sx={{}} />
      </Grid>

      {/* Emails */}
      <Grid size={12}>
        <Box
          sx={{
            overflowX: {
              xs: 'auto',
              lg: 'visible',
            },
            maxWidth: '100%',
          }}
        >
          <Box
            sx={{
              minWidth: {
                xs: 1200,
                lg: 'auto',
              },
              width: '100%',
            }}
          >
            <Grid container spacing={1}>
              <Grid
                size={
                  {
                    xs: 2,
                    lg: 6,
                  }
                }
              >
                <Typography variant="subtitle2">
                  <Translate expression='client.ClientDetail.Relationships.Contacts.Email' />
                </Typography>
              </Grid>
              <Grid
                size={1.5}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="subtitle2">
                  <Translate expression='client.ClientDetail.Relationships.Contacts.Channels.Notification' />
                </Typography>
              </Grid>
              <Grid
                size={1.5}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="subtitle2">
                  <Translate expression='client.ClientDetail.Relationships.Contacts.Channels.Authentication' />
                </Typography>
              </Grid>
              <Grid size={{xs: 7, lg: 0}}></Grid>
              {
                  Object.keys(contactsTable.emails).length === 0 ? (
                    <Grid size={12}>
                      <Typography>
                        <Translate expression='client.ClientDetail.Relationships.Contacts.NoneAdded' />
                      </Typography>
                    </Grid>
                  ) : (
                    Object.entries(contactsTable.emails).map(([email, metadata]) => {
                      return (
                        <React.Fragment key={email}>
                          <Grid
                            size={
                              {
                                xs: 2,
                                lg: 6,
                              }
                            }
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                maxWidth: '100%',
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-all',
                              }}
                            >
                              {email}
                            </Typography>
                          </Grid>
                          <Grid
                            size={1.5}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <EmailNotification
                              clientBackend={clientBackend}
                              emailData={{
                                email: email,
                                isSubscribedForNotification: metadata.isSubscribedForNotification,
                              }}
                              relationshipData={relationshipData}
                            />
                          </Grid>
                          <Grid
                            size={1.5}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <ClientAuthentication
                              clientBackend={clientBackend}
                              relationshipData={relationshipData}
                              userIdentity={{
                                email: email,
                                status: metadata.authenticationStatus,
                              }}
                            />
                          </Grid>
                          <Grid size={{xs: 7, lg: 0}}></Grid>
                        </React.Fragment>
                      );
                    })
                  )
              }
            </Grid>
          </Box>
        </Box>
      </Grid>
      <Grid size={12}>
        <Divider sx={{}} />
      </Grid>

      {/* Phone Numbers */}
      <Grid size={12}>
        <Box
          sx={{
            overflowX: {
              xs: 'auto',
              lg: 'visible',
            },
            maxWidth: '100%',
          }}
        >
          <Box
            sx={{
              minWidth: {
                xs: 1200,
                lg: 'auto',
              },
              width: '100%',
            }}
          >
            <Grid container spacing={1}>
              <Grid
                size={
                  {
                    xs: 2,
                    lg: 6,
                  }
                }
              >
                <Typography variant="subtitle2">
                  <Translate expression='client.ClientDetail.Relationships.Contacts.Phone' />
                </Typography>
              </Grid>
              <Grid
                size={1.5}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="subtitle2">
                  <Translate expression='client.ClientDetail.Relationships.Contacts.Channels.Notification' />
                </Typography>
              </Grid>
              <Grid
                size={1.5}
              ></Grid>
              <Grid
                size={1.5}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="subtitle2">
                  <Translate expression='client.ClientDetail.Relationships.Contacts.Channels.Communication' />
                </Typography>
              </Grid>
              <Grid
                size={1.5}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="subtitle2">
                  <Translate expression='client.ClientDetail.Relationships.Contacts.Channels.Dialpad' />
                </Typography>
              </Grid>
              {
              isBreakpointLg ? null :
              <Grid
                size={{xs: 4, lg: 0}}
              ></Grid>
              }
              {
                  Object.keys(contactsTable.phoneNumbers).length === 0 ? (
                    <Grid
                      size={12}
                    >
                      <Typography>
                        <Translate expression='client.ClientDetail.Relationships.Contacts.NoneAdded' />
                      </Typography>
                    </Grid>
                  ) : (
                    Object.entries(contactsTable.phoneNumbers).map(([phoneNumber, metadata]) => {
                      return (
                        <React.Fragment key={phoneNumber}>
                          <Grid
                            size={
                              {
                                xs: 2,
                                lg: 6,
                              }
                            }
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                maxWidth: '100%',
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-all',
                              }}
                            >
                              {phoneNumber}
                            </Typography>
                          </Grid>
                          <Grid
                            size={1.5}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <SmsNotification
                              clientBackend={clientBackend}
                              smsData={{
                                smsNumber: phoneNumber,
                                isSubscribedForNotification: metadata.isSubscribedForNotification,
                              }}
                              relationshipData={relationshipData}
                            />
                          </Grid>
                          <Grid
                            size={1.5}
                          ></Grid>
                          <Grid
                            size={1.5}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <SmsCommunication
                              clientBackend={clientBackend}
                              smsData={{
                                smsNumber: phoneNumber,
                                communicationStatus: metadata.communicationStatus,
                              }}
                              relationshipData={relationshipData}
                            />
                          </Grid>
                          <Grid
                            size={1.5}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <SmsDialpadIntegration
                              clientBackend={clientBackend}
                              smsData={{
                                smsNumber: phoneNumber,
                                dialpadStatus: metadata.dialpadStatus,
                              }}
                              relationshipData={relationshipData}
                            />
                          </Grid>
                          {
                            isBreakpointLg ? null :
                            <Grid
                              size={{xs: 4, lg: 0}}
                            ></Grid>
                          }
                        </React.Fragment>
                      );
                    })
                  )
              }
            </Grid>
          </Box>
        </Box>
      </Grid>

      {/* Miscellaneous */}
      {
          Object.keys(contactsTable.miscellaneous).length === 0 ? null : (
          <>
            <Grid size={12}>
              <Divider sx={{}} />
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2">
                <Translate expression='client.ClientDetail.Relationships.Contacts.Miscellaneous' />
              </Typography>
            </Grid>
            {
              Object.entries(contactsTable.miscellaneous).flatMap(([type, values]) => {
                return values.map((value, index) => {
                  return (
                    <Grid
                      size={12}
                      key={`${type}-${value}-${index}`}
                    >
                      <Stack
                        direction={'row'}
                        spacing={1}
                      >
                        <Typography
                          sx={{
                            fontWeight: 500,
                          }}
                        >
                          {type}
                        </Typography>
                        <Typography
                          sx={{}}
                        >
                          {value}
                        </Typography>
                      </Stack>
                    </Grid>
                  );
                });
              })
            }
          </>
        )
      }
    </Grid>
  );
};

Contacts.propTypes = {
  relationshipData: PropTypes.object.isRequired,
  clientBackend: PropTypes.object.isRequired,
  userIdentity: PropTypes.object,
  userNotificationChannels: PropTypes.shape({
    emails: PropTypes.array,
    smsNumbers: PropTypes.array,
  }),
  userCommunication: PropTypes.object,
  userDialpadIntegration: PropTypes.object,
  clientAuthenticationLoading: PropTypes.bool.isRequired,
  clientNotificationLoading: PropTypes.bool.isRequired,
  clientCommunicationLoading: PropTypes.bool.isRequired,
  clientDialpadIntegrationLoading: PropTypes.bool.isRequired,
};

export default React.memo(Contacts);
